import {mapGetters, mapMutations, mapActions} from "vuex"

export default {
    computed: {
        ...mapGetters([
            "viewingAPUserDataFor",
            "purchaseOrdersFormattedList",
            "apUserId",
            "currentUser",
            "rolesObj",
            "rolesToNamesMap",
            "userData",
            "poTableUserPreferences",
            "poFiltersList"
        ])
    },
    data() {
        return {
            DIRECT_REPORT_STRING: 'Requested by my direct reports',
            CREATED_BY_STRING: 'Purchase orders created by me',
            REQUESTED_BY_STRING: 'Purchase orders requested by me',
            COST_CENTER_OWNER_STRING: 'Cost center owner',
            COST_CENTER_FBP_STRING: 'Cost center finance business partner',
            componentKey:0
        }
    },
    methods: {
        ...mapMutations([
            "setRolesToNamesMap",
            "setPurchaseOrdersFormattedList",
            "setInvoicesTableUserPreferences",
            "setRolesObj",
            "setIsLoading",
            "setPOTableUserPreferences",
            "setIsNamesFilterLoading"
        ]),
        ...mapActions(['getDirectReportsById']),
        isDirectReportUser(directReportsObj, userId) {
            if(directReportsObj && Object.keys(directReportsObj).length > 0) {
                return directReportsObj.some(e => e.id === userId)
            }
            return false
        },
        async getAllNamesForFilter() {
            /*
            * This method does the following:
            * - Gets the list of direct report users if any
            * - Checks if user has direct reports, if not - exits method.
            * - Creates a rolesToNamesMap which has Direct report fullName present (All other items are not used)
            * - Sets full names (only for direct report users) which were fetched via teams api on the po data for filtering.
            * - Updates rolesObj (list of roles for current user) based on current user if they are createdBy, costCenterOwner, costCenterFbp or directReports
            * - Sets namesFilterLoading to false, to notify user names filters are ready to use.
            * */
            this.setIsLoading(true)

            this.resetNamesData()

            let directReportsUsers = []

            let userId = this.viewingAPUserDataFor ? this.apUserId : this.currentUser.userName

            directReportsUsers = await this.getDirectReportsById(userId)

            if(directReportsUsers && directReportsUsers.length > 0) {

                let uniqueUsers = this.getUniqueUsers(directReportsUsers)

                //Maintain a map for direct reports and corresponding users.
                let rolesToNameMap = { directReports: [] }

                //Update full name so we don't have to re-fetch it via userInfo call.
                this.purchaseOrdersFormattedList.forEach((po) => {
                    let directReportExist = directReportsUsers.find((user) => user.id === po.requestedBy)
                    if(directReportExist)
                        po.requestedByFullName = directReportExist.fullName
                })

                if(uniqueUsers.directReportIds && uniqueUsers.directReportIds.length > 0) {
                    rolesToNameMap["directReports"] = Object.values(directReportsUsers).map((users) => users.fullName)
                    this.rolesObj["directReports"] = this.DIRECT_REPORT_STRING
                }

                this.setRolesToNamesMap(rolesToNameMap)

            }

            if(!this.validateIfUserIs('costCenterOwner', userId)) {
                delete this.rolesObj.costCenterOwner
            }

            if(!this.validateIfUserIs('requestedBy', userId)) {
                delete this.rolesObj.requestedBy
            }

            if(!this.validateIfUserIs('costCenterFbp', userId)) {
                delete this.rolesObj.costCenterFbp
            }

            if(!this.validateIfUserIs('createdBy', userId)) {
                delete this.rolesObj.createdBy
            }

            this.setRolesObj(this.rolesObj)

            if(Object.keys(this.poTableUserPreferences).length === 0) {
                this.resetPoTableUserPreferences()
            }

            this.poTableUserPreferences.filter.roles = this.poFiltersList.isFiltersReloaded ? this.poTableUserPreferences.filter.roles : Object.values(this.rolesObj)

            this.setPOTableUserPreferences(this.poTableUserPreferences)

            this.setIsLoading(false)
            this.setIsNamesFilterLoading(false)
        },
        resetPoTableUserPreferences() {
            let resetUserPreferences = {
                pagination: {
                    currentPage: '1',
                    currentPerPage: '10',
                },
                sort: {
                    field: 'docDate',
                    type: 'desc',
                    multipleColumns: false
                },
                filter: {
                    supplierName: [],
                    originalStatus: [],
                    costCenter: [],
                    names: this.rolesToNamesMap.directReports && this.rolesToNamesMap.directReports.length > 0 ? Object.values(this.rolesToNamesMap.directReports) : [],
                    roles: Object.values(this.rolesObj),
                    supplierNameAllUnchecked: false,
                    namesAllUnchecked: false,
                    rolesAllUnchecked: false,
                    originalStatusAllUnchecked: false,
                    costCenterAllUnchecked: false
                },
                userClickedSort: false,
                showClearFilters: false
            }
            this.setPOTableUserPreferences(resetUserPreferences)
            this.componentKey++
        },
        resetInvoiceTableUserPreferences() {
            let resetUserPreferences = {
                pagination: {
                    currentPage: '1',
                    currentPerPage: '10',
                },
                sort: {
                    field: 'invoiceDate',
                    type: 'desc',
                    multipleColumns: false
                },
                filter: {
                    supplierName: [],
                    supplierNameAllUnchecked: false,
                    invoiceStatus: [],
                    invoiceStatusAllUnchecked: false,
                    invoiceType: [],
                    invoiceTypeAllUnchecked: false,
                    roles: [],
                    rolesAllUnchecked: false,
                    invoicesDirectReports: [],
                    invoicesDirectReportsAllUnchecked: false
                },
                userClickedSort: false,
                showClearFilters: false
            }
            this.setInvoicesTableUserPreferences(resetUserPreferences)
        },
        validateIfUserIs(userRole, userId) {
            if(userRole === 'createdBy') {
                return this.purchaseOrdersFormattedList.some((po) => po.createdBy === userId)
            } else if(userRole === 'requestedBy') {
                return this.purchaseOrdersFormattedList.some((po) => po.requestedBy === userId)
             } else {
                return this.purchaseOrdersFormattedList.some((po) => {
                    if(po.poAdditionalData && po.poAdditionalData.length > 0) {
                        return po.poAdditionalData.some((data) => {
                            return data[userRole] === userId
                        })
                    }
                })
            }
        },
        getUniqueUsers(directReportsUsers) {
            //Get unique names from PO Array, so that one user call is done per name and not repeated
            //get direct report names if user has direct reports.
            let uniqueIds =
                {
                    directReportIds: []
                }

            this.purchaseOrdersFormattedList.forEach((po) => {
                this.isDirectReportUser(directReportsUsers, po.requestedBy) ? uniqueIds.directReportIds.push(po.requestedBy) : ''
            })
            return uniqueIds
        },
        resetNamesData() {
            this.setIsNamesFilterLoading(true)
            this.setRolesToNamesMap([])
            this.setRolesObj({
                "requestedBy" : this.REQUESTED_BY_STRING,
                "createdBy" : this.CREATED_BY_STRING,
                "costCenterOwner" : this.COST_CENTER_OWNER_STRING,
                "costCenterFbp" : this.COST_CENTER_FBP_STRING
            })
        }
    }
}